// Shared diagnostic logger for WIN PWA apps.
//
// Writes structured log entries to IndexedDB (app_log table).
// No count-based cap -- purgeOldLogs() (age-based, TTL) is the only cleanup.
//
// Each app creates its own logger instance via createLogger(), passing
// its Dexie database. The table name is always "app_log".
//
// Usage:
//   import { createLogger } from "./app_logger"
//   import db from "./field_db"
//   const { logError, logEvent, logWarn, logInfo, setLogContext } = createLogger(db)

const SYNCED_TTL_MS   = 7  * 24 * 60 * 60 * 1000  // 7 days -- purge synced entries
const UNSYNCED_TTL_MS = 30 * 24 * 60 * 60 * 1000  // 30 days -- safety net for stuck unsynced entries

// Computed once at module load -- cheap, doesn't change during a session.
const _platform = detectPlatformSnapshot()

// Ambient context -- the id of the place/mission/response currently in view.
// Callers update it via setLogContext() as the tasker moves between records,
// rather than threading these ids through every logEvent/logError call site.
let _context = { placeId: null, missionId: null, responseId: null }

export function setLogContext(partial = {}) {
  _context = { ..._context, ...partial }
}

// Read-only copies of the ambient context and the platform snapshot, for
// callers that need to attach the same facts to something other than a log
// entry -- e.g. a Raise a Ticket submission (#2617). Copies, so a caller can
// never mutate the logger's own state.
export function getLogContext() {
  return { ..._context }
}

export function getPlatformSnapshot() {
  return { ..._platform }
}

function detectPlatformSnapshot() {
  const ua = navigator.userAgent || ""

  let os = "unknown"
  const iosMatch = ua.match(/(?:iPhone|iPad|iPod).*OS (\d+)[_.](\d+)/)
  const androidMatch = ua.match(/Android (\d+(?:\.\d+)?)/)
  const macMatch = ua.match(/Mac OS X (\d+)[_.](\d+)/)

  if (iosMatch) {
    os = `iOS ${iosMatch[1]}.${iosMatch[2]}`
  } else if (androidMatch) {
    os = `Android ${androidMatch[1]}`
  } else if (macMatch) {
    os = `macOS ${macMatch[1]}.${macMatch[2]}`
  } else if (/Windows/.test(ua)) {
    os = "Windows"
  } else if (/Linux/.test(ua)) {
    os = "Linux"
  }

  const displayMode = typeof window === "undefined"
    ? "unknown"
    : window.matchMedia?.("(display-mode: standalone)").matches
      ? "standalone"
      : "browser"

  return { os, displayMode }
}

// JSON.stringify throws on a cyclic object (a DOM node, an error with a
// circular cause). That used to lose the whole log entry, so fall back to a
// copy that marks repeats, and finally to a plain note, never to a throw
// (card #3996).
function _safeStringify(value) {
  try {
    return JSON.stringify(value)
  } catch {
    try {
      const seen = new WeakSet()
      return JSON.stringify(value, (_key, item) => {
        if (typeof item === "object" && item !== null) {
          if (seen.has(item)) return "[Circular]"
          seen.add(item)
        }
        return item
      })
    } catch {
      return JSON.stringify({ unserialisable: true })
    }
  }
}

export function createLogger(db, tableName = "app_log") {
  // In-memory only -- a write failure means the entry never reached IndexedDB
  // at all, so there's nowhere durable to record the drop itself. Reset every
  // page load; emitQueueSnapshot (field_image_queue.js, Card #3799) reads this
  // each pass so a device that's silently losing logs to a full quota still
  // surfaces that fact in the next snapshot it DOES manage to write.
  let droppedCount = 0

  function logError(category, message, detail = null) {
    return _write("error", category, message, detail)
  }

  function logWarn(category, message, detail = null) {
    return _write("warn", category, message, detail)
  }

  function logInfo(category, message, detail = null) {
    return _write("info", category, message, detail)
  }

  function logEvent(category, message, detail = null) {
    return _write("event", category, message, detail)
  }

  async function purgeOldLogs() {
    try {
      const syncedCutoff   = new Date(Date.now() - SYNCED_TTL_MS).toISOString()
      const unsyncedCutoff = new Date(Date.now() - UNSYNCED_TTL_MS).toISOString()

      const toDelete = await db[tableName]
        .filter(r =>
          (r.syncedAt  && r.timestamp < syncedCutoff) ||
          (!r.syncedAt && r.timestamp < unsyncedCutoff)
        )
        .primaryKeys()

      if (toDelete.length > 0) await db[tableName].bulkDelete(toDelete)
    } catch { /* never throw */ }
  }

  function installGlobalErrorHandlers() {
    window.addEventListener("error", (e) => {
      logError("app", e.message || "Uncaught error", {
        filename: e.filename,
        lineno:   e.lineno,
        colno:    e.colno,
        stack:    e.error?.stack?.slice(0, 500)
      }).catch(() => {})
    })

    window.addEventListener("unhandledrejection", (e) => {
      const reason = e.reason
      const message = reason?.message || String(reason) || "Unhandled promise rejection"
      logError("app", message, {
        stack: reason?.stack?.slice(0, 500)
      }).catch(() => {})
    })
  }

  function getDroppedLogCount() {
    return droppedCount
  }

  async function _write(level, category, message, detail) {
    try {
      await db[tableName].add({
        level,
        category,
        message:     String(message).slice(0, 500),
        detail:      detail ? _safeStringify(detail).slice(0, 1000) : null,
        timestamp:   new Date().toISOString(),
        syncedAt:    null,
        placeId:     _context.placeId,
        missionId:   _context.missionId,
        responseId:  _context.responseId,
        platformOs:  _platform.os,
        displayMode: _platform.displayMode
      })
    } catch (error) {
      // Logger must never throw -- silently swallow all errors. QuotaExceeded
      // is the one failure worth counting: it means the device's storage is
      // full enough that logs themselves are being lost (Card #3799).
      if (error?.name === "QuotaExceededError") droppedCount++
    }
  }

  return {
    logError, logWarn, logInfo, logEvent,
    purgeOldLogs, installGlobalErrorHandlers, setLogContext,
    getDroppedLogCount
  }
}
